/*
 * What a free-design widget needs from the page around it: which screen is
 * showing, how to switch screens, where uploaded images live, and whether
 * the editor is showing it (then buttons must not write).
 *
 * The live page, the editor and the standalone runtime on a gateway each
 * provide their own. Without a provider the defaults keep widgets harmless.
 */

import { createContext } from 'preact';
import { useContext } from 'preact/hooks';

export interface HmiScreenRef {
  id: string;
  label: string;
  iconRef?: string;
}

export interface HmiRuntime {
  screens: readonly HmiScreenRef[];
  currentScreenId: string | null;
  navigate(screenId: string): void;
  /** URL of an uploaded HMI asset (cloud: /api/hmi-assets/{id}/content,
   *  gateway: /api/v1/hmi/assets/{id}). */
  assetUrl(assetId: string): string;
  /** Editor canvas: show, do not act. */
  editing: boolean;
  /** Light or dark as shown right now. */
  scheme: 'light' | 'dark';
  /** Switch light/dark on this device. Absent: the page offers no choice. */
  setScheme?(scheme: 'light' | 'dark'): void;
  /** Einladung ins Registrierportal holen (QR-Code „Benutzer hinzufügen“).
   *  Nur die lokale Laufzeit am Bedienfeld bietet das an; die Web-HMI nie. */
  createInvitation?(): Promise<PanelInvitation>;
}

export interface PanelInvitation {
  url: string;
  expiresAt: string;
}

export const DEFAULT_RUNTIME: HmiRuntime = {
  screens: [],
  currentScreenId: null,
  navigate: () => {},
  assetUrl: (id) => `/api/hmi-assets/${encodeURIComponent(id)}/content`,
  editing: false,
  scheme: 'dark',
};

const RuntimeContext = createContext<HmiRuntime>(DEFAULT_RUNTIME);

export const HmiRuntimeProvider = RuntimeContext.Provider;

export function useHmiRuntime(): HmiRuntime {
  return useContext(RuntimeContext);
}
